import { del, get, post, put } from '@api/api';
import {
	QueryClient,
	useMutation,
	useQuery,
	useQueryClient
} from '@tanstack/react-query';
import { AnalyticsActionName } from '@utils/analytics';
import API_ROUTES from 'api/routes';
import useAnalytics from 'hooks/useAnalytics';
import {
	AccessKeyStatus,
	IAccessKey,
	IGenerateAccessKey,
	IGeneratedAccessKey
} from 'types/accessKeys';
import { IDataResponse } from 'types/common';
import QueryKeys from '.';

const onKeyAccessUpdated =
	(queryClient: QueryClient) => (res: IDataResponse<IAccessKey>) => {
		const updatedKey = res.data ? res.data[0] : null;
		const allAccessKeys = queryClient.getQueryData<IDataResponse<IAccessKey>>([
			QueryKeys.accessKeys
		]);
		if (updatedKey && allAccessKeys) {
			queryClient.setQueryData([QueryKeys.accessKeys], {
				...allAccessKeys,
				data: (allAccessKeys.data || []).map((key) =>
					key.id === updatedKey.id ? updatedKey : key
				)
			});
		}
	};

const getAccessKeys = async () => {
	const res = await get<IDataResponse<IAccessKey>>(
		API_ROUTES.v1.accessKeys.all
	);
	return res.data;
};

const editAccessKey = async (key: IAccessKey) => {
	const res = await put<IAccessKey, IDataResponse<IAccessKey>>(
		API_ROUTES.v1.accessKeys.update,
		key
	);
	return res.data;
};

const deleteAccessKeyDel = async (key: string) => {
	await del(API_ROUTES.v1.accessKeys.delete(key));
	return key;
};

const deactivate = async (key: string) => {
	const res = await post<{ ids: string[] }, IDataResponse<IAccessKey>>(
		API_ROUTES.v1.accessKeys.deactivate,
		{
			ids: [key]
		}
	);
	return res.data;
};

const activateKey = async (key: string) => {
	const res = await post<{ ids: string[] }, IDataResponse<IAccessKey>>(
		API_ROUTES.v1.accessKeys.activate,
		{
			ids: [key]
		}
	);
	return res.data;
};

const generateAccessKeyPost = async (key: IGenerateAccessKey) => {
	const res = await post<IGenerateAccessKey, IGeneratedAccessKey>(
		API_ROUTES.v1.accessKeys.generate,
		key
	);
	return res.data;
};

export const useGenerateAccessKey = () => {
	const queryClient = useQueryClient();
	const track = useAnalytics();
	return useMutation<IGeneratedAccessKey, unknown, IGenerateAccessKey>(
		async (value) => generateAccessKeyPost(value),
		{
			onSuccess: ({ key }) => {
				const allAccessKeys = queryClient.getQueryData<
					IDataResponse<IAccessKey>
				>([QueryKeys.accessKeys]);
				if (allAccessKeys) {
					queryClient.setQueryData([QueryKeys.accessKeys], {
						...allAccessKeys,
						data: [...(allAccessKeys.data || []), key],
						total: allAccessKeys.total + 1
					});
				}

				track(AnalyticsActionName.accessKeyCreation);
			}
		}
	);
};

export const useUpdateAccessKeyStatus = () => {
	const queryClient = useQueryClient();
	return useMutation<
		IDataResponse<IAccessKey>,
		unknown,
		{ key: string; status: AccessKeyStatus }
	>(
		async ({ key, status }) =>
			status === AccessKeyStatus.active ? deactivate(key) : activateKey(key),
		{
			onSuccess: onKeyAccessUpdated(queryClient)
		}
	);
};

export const useUpdateAccessKey = () => {
	const queryClient = useQueryClient();
	return useMutation<IDataResponse<IAccessKey>, unknown, IAccessKey>(
		async (key: IAccessKey) => editAccessKey(key),
		{
			onSuccess: onKeyAccessUpdated(queryClient)
		}
	);
};

export const useDeleteAccessKey = () => {
	const queryClient = useQueryClient();

	return useMutation<string, unknown, string>(
		async (key: string) => deleteAccessKeyDel(key),
		{
			onSuccess: (deleteKeyId: string) => {
				const allAccessKeys = queryClient.getQueryData<
					IDataResponse<IAccessKey>
				>([QueryKeys.accessKeys]);
				if (allAccessKeys) {
					queryClient.setQueryData([QueryKeys.accessKeys], {
						...allAccessKeys,
						data: (allAccessKeys.data || []).filter(
							(item) => item.id !== deleteKeyId
						),
						total: allAccessKeys.total - 1
					});
				}
			}
		}
	);
};

export const useAccessKeys = () =>
	useQuery([QueryKeys.accessKeys], getAccessKeys, { staleTime: 100000 });
